import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl } from "shared/lib/utils";

import { HomeV2SectionStoryBlock } from "scmp-app/components/home-v2/section-story-block";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type {
  ArticleRecirculationWidgetName,
  RecirculationWidgetImpressionEvent,
} from "scmp-app/components/tracking/google-analytics-4/types";
import type { FallbackReason, WidgetVersion } from "scmp-app/pages/home/ab-widget-fetch";
import type { spotlightHomeV2Query$key } from "scmp-app/queries/__generated__/spotlightHomeV2Query.graphql";

import { Container, SectionTitle } from "./styles";

type Props = {
  className?: string;
  fallbackReason?: FallbackReason;
  reference: spotlightHomeV2Query$key;
  widgetName?: ArticleRecirculationWidgetName;
  widgetVersion?: WidgetVersion;
};

export const HomeV2Spotlight: FunctionComponent<Props> = ({
  className,
  fallbackReason,
  reference,
  widgetName = "scmp_spotlight",
  widgetVersion,
}) => {
  const data = useFragment(
    graphql`
      fragment spotlightHomeV2Query on Query
      @argumentDefinitions(spotlightQueueName: { type: "String!" }) {
        spotlightV2Queue: queue(filter: { name: $spotlightQueueName }) {
          items(first: 4) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...sectionStoryBlockHomeV2QueueItemsEdge
            }
          }
        }
      }
    `,
    reference,
  );

  const items = data.spotlightV2Queue?.items?.edges ?? [];
  const listOfArticles = items.map(item => item.node.entityId).join(",");

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        result: fallbackReason ? "error" : "valid",
        ...(fallbackReason && { result_error: fallbackReason }),
        widget_name: widgetName,
        ...(widgetVersion && { widget_version: widgetVersion }),
      },
      subcategory: "widget",
    }),
    [currentPageType, fallbackReason, listOfArticles, widgetName, widgetVersion],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (items.length === 0) return null;

  return (
    <BaseLinkContextProvider customQueryParameters={{ module: tracking.module.Spotlight }}>
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <HomeV2SectionStoryBlock
          header={<SectionTitle>SCMP Spotlight</SectionTitle>}
          onClick={(entityId, urlAlias) => {
            sendGA4Tracking({
              action: "click",
              category: "recirculation",
              customized_parameters: {
                article_id: entityId,
                destination_url: getAbsoluteUrl(urlAlias),
                list_of_articles: listOfArticles,
                page_type: currentPageType,
                recommendation_id: "",
                result: fallbackReason ? "error" : "valid",
                ...(fallbackReason && { result_error: fallbackReason }),
                widget_name: widgetName,
                ...(widgetVersion && { widget_version: widgetVersion }),
              },
              subcategory: "widget",
            });
          }}
          reference={items}
        />
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2Spotlight.displayName = "HomeV2Spotlight";
